import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import DataTable, { SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadViewData from "@tSurya/Handlers/ViewReturnData";
import { ViewReturns } from "@tSurya/Services/ViewReturnListService";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import SuryaPageThemeWrapper from "@tSurya/Utils/SuryaPageThemeWrapper";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    return {
        userData: state.SURData.viewDataStore,
        parameterStore: state.SURData.viewDataStore,
        isLoading: state.SURData.viewDataStore.isLoading,
        location,
    };
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    loadViewData,
};

export interface SurViewReturnsStyle {
    container?: InjectableCss;
    button?: InjectableCss;
    textField?: InjectableCss;
    dateField?: InjectableCss;
    mainContainer?: InjectableCss;
    tableHeader?: InjectableCss;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    link?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    tableRow?: InjectableCss;
    totalCreditAmount?: DataTableCellBaseProps;
}

export const surViewReturns: SurViewReturnsStyle = {
    mainContainer: {
        css: css`
            [class^="DataTableCellBase"] {
                font-size: 15px;
            }
            margin-top: 50px;
            @media print {
                table {
                    display: table !important;
                }

                tbody {
                    display: table-row-group !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        overflow-x: scroll;
                    `,
                    css`
                        overflow-x: scroll;
                    `,
                    css`
                        overflow-x: scroll;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    button: {
        css: css`
            padding-top: 23px;
            button[class^="ButtonWrapper"] {
                border-radius: 0px;
                background: ${getColor("common.background")};
                border: 2px solid ${COLORS.text.main};
                color: ${getColor("text.main")};
            }
        `,
    },

    dateField: {
        css: css`
            div[class^="FormFieldStyle"] {
                label {
                    font-family: "Mulish";
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    textField: {
        css: css`
            div[class^="FormFieldStyle"] {
                label {
                    font-family: "Mulish";
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    color: ${getColor("text.main")};
                }
            }

            div[class^="FormInputWrapper"] {
                input {
                    background: ${getColor("common.background")};
                    border: 1px solid ${getColor("common.border")} !important;
                }
            }
        `,
    },
    tableRow: {
        css: css`
            td {
                background: ${getColor("common.background")};
                border: 1px solid ${getColor("common.border")};
            }
            tr {
                border: 1px solid ${getColor("common.border")};
            }
        `,
    },
    tableHeader: {
        css: css`
            th {
                white-space: nowrap;
                color: ${getColor("text.main")};
                border: 1px solid ${getColor("common.border")};
            }
            td {
                white-space: nowrap;
                font-size: 15px;
                color: ${getColor("text.main")};
            }
            tr {
                white-space: nowrap;
                color: ${getColor("text.main")};
                border: 1px solid ${getColor("common.border")};
            }
            button {
                justify-content: center;
            }
            span[class^="TypographyStyle"] {
                color: ${getColor("text.main")};
                text-align: center;
            }
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    link: {
        css: css`
            span {
                font-size: 15px;
                color: ${getColor("text.link")} !important;
                &:hover {
                    text-align: center;
                    text-decoration-line: underline;
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    totalCreditAmount: {
        alignX: "right",
        css: css`
            text-align: right !important;
        `,
    },
};

const styles = surViewReturns;
export const SurViewReturns = ({ loadViewData, userData, parameterStore, location, isLoading }: Props) => {
    const viewReturn = userData.viewdata.viewReturnList;
    const history = useHistory();
    const serachedParameter = parameterStore.searchparameter;
    
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    useEffect(() => {
        if (typeof window !== "undefined") {
            window?.scrollTo(0, 0);
        }
    });

    React.useEffect(() => {
        loadViewData(queryParams);
    }, []);
    useEffect(() => {
        const queryString = qs.stringify(serachedParameter);
        if (queryString) {
            history.push(`${location.pathname}?${queryString}`);
        } else {
            history.push(location.pathname);
        }
    }, [serachedParameter]);
    const [sortfieldData, setsortfieldData] = useState("");
    const [sortedState, setSortedState] = useState<boolean | "asc" | "desc">("asc");

    const headerClick = (sortField: string) => {
        setSortedState(sortedState === "asc" ? "desc" : "asc");
        const sort = sortfieldData === sortField ? `${sortField} DESC` : sortField;
        setsortfieldData(sort);
        loadViewData({ ...serachedParameter, sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (sortfieldData === sortField) {
            sorted = "ascending";
        } else if (sortfieldData === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    if (isLoading) {
        return (
            <SuryaPageThemeWrapper>
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            </SuryaPageThemeWrapper>
        );
    }

    return (
        <SuryaPageThemeWrapper>
            <StyledWrapper>
                {viewReturn?.length ? (
                    <StyledWrapper {...styles.mainContainer}>
                        <DataTable>
                            <DataTableHead {...styles.tableHeader}>
                                <DataTableHeader sorted={sorted("RmaNumber")} onSortClick={() => headerClick("RmaNumber")}>
                                    {translate("Return Request No")}
                                </DataTableHeader>
                                <DataTableHeader sorted={sorted("RMADate")} onSortClick={() => headerClick("RMADate")}>
                                    {translate("Return Request Date")}
                                </DataTableHeader>
                                <DataTableHeader sorted={sorted("Status")} onSortClick={() => headerClick("Status")}>
                                    {translate("Status")}
                                </DataTableHeader>
                                {/* <DataTableHeader sorted={sorted("WebRMAId")} onSortClick={() => headerClick("WebRMAId")}>
                                    {translate("Web Return Request ID")}
                                </DataTableHeader> */}
                                <DataTableHeader
                                    sorted={sorted("TotalCreditAmount")}
                                    onSortClick={() => headerClick("TotalCreditAmount")}
                                >
                                    {translate("Total Credit Amount")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {viewReturn?.map((item: ViewReturns, index: React.Key | undefined) => {
                                    return (
                                        <DataTableRow key={Number(index)} {...styles.tableRow}>
                                            <DataTableCell>
                                                <Link
                                                    href={`${location.pathname}/ViewDetails?rmaNumber=${item.rmaNumber}`}
                                                    {...styles.link}
                                                >
                                                    {item.rmaNumber}
                                                </Link>
                                            </DataTableCell>
                                            <DataTableCell>
                                                {item.rmaDate
                                                    ? new Date(item.rmaDate).toLocaleDateString("en-US", {
                                                            month: "2-digit",
                                                            day: "2-digit",
                                                            year: "numeric",
                                                        })
                                                    : ""}
                                            </DataTableCell>
                                            <DataTableCell>{item.status}</DataTableCell>
                                            {/* <DataTableCell>{item.webRMAId}</DataTableCell> */}
                                            <DataTableCell {...styles.totalCreditAmount}>
                                                {String(item.totalCreditAmount).includes("-")
                                                    ? `$(${Math.abs(item.totalCreditAmount)
                                                        .toFixed(2) 
                                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`
                                                    : item.totalCreditAmountDisplay}
                                            </DataTableCell>
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...styles.noResultsText}>{translate("No Data found")}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </SuryaPageThemeWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SurViewReturns),
    definition: {
        group: "Common",
        icon: "Filter",
        displayName: "SurViewReturns",
        fieldDefinitions: [],
    },
};

export default widgetModule;
